import React, { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '../../hooks/useIsMobile';
import type { ShareConfig, SheetSnap } from './Sheet';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  footer?: React.ReactNode;
  variant?: 'centered' | 'sheet';
  /**
   * When false, backdrop-click and Escape do NOT close the modal — only the
   * explicit X button (and the caller's own buttons) dismiss it. Default true.
   * Use for consequential flows (e.g. admin impersonation) where an accidental
   * backdrop tap must not discard state.
   */
  dismissible?: boolean;
  /**
   * When true, suppresses the explicit X close button entirely (both the
   * titled and untitled header variants). Independent of `dismissible`,
   * which only gates backdrop-click/Escape and (per its own contract, see
   * ImpersonateModal) leaves the X working as the one guaranteed exit.
   * Use only when the caller renders its own explicit exits and an
   * accidental close would be worse than no visible affordance at all
   * (e.g. the 0zzz-fe org picker, where the only valid exits are picking an
   * org or the "Go to Organizations" confirm flow). Default false.
   */
  hideCloseButton?: boolean;
  /** Accessible name when there is no visible `title` (defaults to title, then 'Dialog'). */
  ariaLabel?: string;
  children: React.ReactNode;
  shareConfig?: ShareConfig;
  // Spec 192 — Sheet V2 pass-through (ignored by centered variant).
  snapPoints?: SheetSnap[];
  initialSnap?: SheetSnap;
  autoSnapOnFocus?: boolean;
}

const sizeClasses: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'w-full max-w-sm mx-4',
  md: 'w-full max-w-lg mx-4',
  lg: 'w-full max-w-2xl mx-4',
  xl: 'w-full max-w-3xl mx-4',
};

// Lazy-load Sheet to avoid circular dependency (Sheet imports Modal)
let SheetComponent: React.ComponentType<{
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
  shareConfig?: ShareConfig;
  snapPoints?: SheetSnap[];
  initialSnap?: SheetSnap;
  autoSnapOnFocus?: boolean;
}> | null = null;

function ModalCentered({
  isOpen,
  onClose,
  title,
  size = 'md',
  footer,
  children,
  shareConfig,
  dismissible = true,
  hideCloseButton = false,
  ariaLabel,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  // Focus management
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      // Focus the first focusable element inside the dialog
      const timer = setTimeout(() => {
        const focusable = dialogRef.current?.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        focusable?.focus();
      }, 0);
      return () => clearTimeout(timer);
    } else {
      previousFocusRef.current?.focus();
    }
  }, [isOpen]);

  // Escape key (suppressed when dismissible={false} — X button remains the only chrome dismiss)
  useEffect(() => {
    if (!isOpen || !dismissible) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, dismissible, handleKeyDown]);

  // Lock body scroll
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center animate-in fade-in duration-150"
      onClick={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      {/*
        NOTE: this overlay must NOT carry aria-hidden. It is the backdrop AND the wrapper that
        CONTAINS the dialog below — aria-hidden here removed the entire dialog subtree from the
        accessibility tree (screen readers couldn't reach any desktop Modal/Sheet). The dialog's
        own role="dialog" + aria-modal="true" already conveys modal semantics and inerts the
        background, so no aria-hidden is needed on the wrapper. (Spec 235 a11y fix, 2026-06-06.)
      */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || title || 'Dialog'}
        className={[
          'bg-surface rounded-xl shadow-2xl max-h-[90dvh] flex flex-col',
          'animate-in zoom-in-95 fade-in duration-150',
          sizeClasses[size],
        ].join(' ')}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-6 pt-6 pb-0 flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
            {!hideCloseButton && (
              <button
                type="button"
                onClick={onClose}
                className="min-w-touch min-h-touch flex items-center justify-center rounded-lg text-text-secondary hover:bg-surface hover:text-text-primary transition-colors -mr-2"
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* No title — just a close button top-right (unless hideCloseButton) */}
        {!title && !hideCloseButton && (
          <div className="flex justify-end px-6 pt-4 pb-0 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="min-w-touch min-h-touch flex items-center justify-center rounded-lg text-text-secondary hover:bg-surface hover:text-text-primary transition-colors -mr-2"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}

        {/* Content — scrollable */}
        <div className="px-6 py-4 overflow-y-auto flex-1">{children}</div>

        {/* Footer — fixed below content */}
        {(footer || shareConfig) && (
          <div className="px-6 py-4 border-t border-border-light flex items-center gap-2 flex-shrink-0">
            {shareConfig && (
              <button
                type="button"
                onClick={() => setShareModalOpen(true)}
                className="relative min-w-touch min-h-touch flex items-center justify-center rounded-lg text-text-secondary hover:text-text-primary transition-colors"
                aria-label="Share"
                title="Share"
              >
                {/* Inline share SVG to avoid circular import */}
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                {shareConfig.shareCount != null && shareConfig.shareCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-accent text-text-inverse text-[10px] font-bold flex items-center justify-center leading-none">
                    {shareConfig.shareCount}
                  </span>
                )}
              </button>
            )}
            {footer && <div className="ml-auto flex items-center gap-2">{footer}</div>}
          </div>
        )}

        {/* Share popup (desktop only — mobile uses Sheet slide panel) */}
        {shareConfig && shareModalOpen && (
          // Lazy-load ShareSheet to avoid circular deps. ShareSheet only
          // exports a named binding — `.default` would be undefined and
          // crash with React #130 at click time. See bug-registry.md.
          (() => {
            const SS = require('../sharing/ShareSheet').ShareSheet;
            return (
              <SS
                mode="modal"
                isOpen={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                entityType={shareConfig.entityType}
                entityId={shareConfig.entityId}
                entityTitle={shareConfig.entityTitle}
                onShareSuccess={() => { shareConfig.onShareSuccess?.(); setShareModalOpen(false); }}
              />
            );
          })()
        )}
      </div>
    </div>,
    document.body
  );
}

export function Modal(props: ModalProps) {
  const { variant = 'centered', ...rest } = props;
  const isMobile = useIsMobile();

  // Sheet variant: render as bottom sheet on mobile
  if (variant === 'sheet' && isMobile) {
    // Lazy-load Sheet to avoid circular imports
    if (!SheetComponent) {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      SheetComponent = require('./Sheet').Sheet;
    }
    if (SheetComponent) {
      return (
        <SheetComponent
          isOpen={rest.isOpen}
          onClose={rest.onClose}
          title={rest.title}
          footer={rest.footer}
          shareConfig={rest.shareConfig}
          snapPoints={rest.snapPoints}
          initialSnap={rest.initialSnap}
          autoSnapOnFocus={rest.autoSnapOnFocus}
        >
          {rest.children}
        </SheetComponent>
      );
    }
  }

  return <ModalCentered {...rest} />;
}
